import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { CreditCard, Check, AlertCircle, Loader2, Building2, Shield } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Card input form component
const CardSetupForm = ({ clientSecret, stripeAccountId, onSuccess, businessName }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!stripe || !elements) return;

    setIsProcessing(true);
    setError(null);

    try {
      const { error: setupError, setupIntent } = await stripe.confirmCardSetup(clientSecret, {
        payment_method: {
          card: elements.getElement(CardElement),
        },
      });

      if (setupError) {
        setError(setupError.message);
        setIsProcessing(false);
        return;
      }

      if (setupIntent.status === 'succeeded') {
        onSuccess(setupIntent.id);
      }
    } catch (err) {
      setError('An unexpected error occurred. Please try again.');
      setIsProcessing(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="bg-white rounded-xl border border-zinc-200 p-4">
        <label className="block text-sm font-medium text-zinc-700 mb-3">
          Card Details
        </label>
        <CardElement
          options={{
            style: {
              base: {
                fontSize: '16px',
                color: '#1f2937',
                '::placeholder': { color: '#9ca3af' },
              },
              invalid: { color: '#ef4444' },
            },
          }}
          className="p-3 border border-zinc-200 rounded-lg"
        />
      </div>

      {error && (
        <div className="flex items-center gap-2 p-3 bg-red-50 text-red-700 rounded-lg text-sm">
          <AlertCircle size={16} />
          {error}
        </div>
      )}

      <button
        type="submit"
        disabled={!stripe || isProcessing}
        className="w-full py-4 bg-emerald-500 hover:bg-emerald-600 disabled:bg-zinc-300 text-white font-semibold rounded-xl transition-colors flex items-center justify-center gap-2"
      >
        {isProcessing ? (
          <>
            <Loader2 size={20} className="animate-spin" />
            Saving Card...
          </>
        ) : (
          <>
            <CreditCard size={20} />
            Save Card to {businessName}
          </>
        )}
      </button>

      <div className="flex items-center justify-center gap-2 text-xs text-zinc-500">
        <Shield size={14} />
        Secured by Stripe. Your card details are encrypted.
      </div>
    </form>
  );
};

// Main payment setup page
const PaymentSetupPage = () => {
  const { token } = useParams();
  const [setupInfo, setSetupInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [stripePromise, setStripePromise] = useState(null);
  const [clientSecret, setClientSecret] = useState(null);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    const fetchSetupInfo = async () => {
      try {
        const res = await fetch(`${API_URL}/api/payment-setup/${token}`);
        
        if (!res.ok) {
          const data = await res.json();
          throw new Error(data.detail || 'Invalid or expired link');
        }
        
        const data = await res.json();
        setSetupInfo(data);

        // Initialize Stripe with the connected account
        if (data.stripe_enabled && data.stripe_account_id) {
          const stripeKey = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || 'pk_live_51JRelTIgC6ltqyqJhELqMB8cVBHnXj8hOGhNxuqe2qZR0Q1FLz7LJT0dVYvJULqv8nPNIEOoLtQyKx0DqZm9YFlf00bthZpuG1';
          setStripePromise(loadStripe(stripeKey, {
            stripeAccount: data.stripe_account_id
          }));

          // Create setup intent
          const intentRes = await fetch(`${API_URL}/api/payment-setup/${token}/create-setup-intent`, {
            method: 'POST'
          });
          
          if (intentRes.ok) {
            const intentData = await intentRes.json();
            setClientSecret(intentData.client_secret);
          }
        }
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchSetupInfo();
  }, [token]);

  const handleSuccess = async (setupIntentId) => {
    try {
      await fetch(`${API_URL}/api/payment-setup/${token}/confirm?setup_intent_id=${setupIntentId}`, {
        method: 'POST'
      });
      setSuccess(true);
    } catch (err) {
      console.error('Error confirming setup:', err);
      setSuccess(true); // Still show success as card was saved
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-zinc-50 to-zinc-100 flex items-center justify-center p-4">
        <div className="text-center">
          <Loader2 size={40} className="animate-spin text-emerald-500 mx-auto mb-4" />
          <p className="text-zinc-600">Loading payment setup...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-zinc-50 to-zinc-100 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full text-center">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <AlertCircle size={32} className="text-red-500" />
          </div>
          <h1 className="text-xl font-bold text-zinc-900 mb-2">Link Not Valid</h1>
          <p className="text-zinc-600">{error}</p>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-zinc-50 to-zinc-100 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full text-center">
          <div className="w-20 h-20 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <Check size={40} className="text-emerald-500" />
          </div>
          <h1 className="text-2xl font-bold text-zinc-900 mb-2">Card Saved Successfully!</h1>
          <p className="text-zinc-600 mb-6">
            Your payment method has been saved to {setupInfo.business_name}. 
            You can close this page now.
          </p>
          <div className="bg-emerald-50 rounded-xl p-4 text-sm text-emerald-700">
            Future payments will be charged automatically to your saved card.
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-zinc-50 to-zinc-100 py-8 px-4">
      <div className="max-w-md mx-auto">
        {/* Header */}
        <div className="bg-zinc-900 text-white rounded-2xl p-6 mb-6 text-center">
          {setupInfo.business_logo ? (
            <img 
              src={setupInfo.business_logo} 
              alt={setupInfo.business_name}
              className="h-12 mx-auto mb-4 rounded-lg"
            />
          ) : (
            <div className="w-16 h-16 bg-emerald-500 rounded-xl flex items-center justify-center mx-auto mb-4">
              <Building2 size={32} className="text-white" />
            </div>
          )}
          <h1 className="text-xl font-bold mb-1">
            Set up your payment details with
          </h1>
          <p className="text-2xl font-bold text-emerald-400">
            {setupInfo.business_name}
          </p>
        </div>

        {/* Customer info */}
        <div className="bg-white rounded-2xl shadow-lg p-6 mb-6">
          <h2 className="text-lg font-semibold text-emerald-600 mb-2">
            Payment Details for {setupInfo.customer_name}
          </h2>
          {setupInfo.customer_email && (
            <p className="text-sm text-zinc-500">{setupInfo.customer_email}</p>
          )}
        </div>

        {/* Payment form */}
        {setupInfo.stripe_enabled && stripePromise && clientSecret ? (
          <div className="bg-white rounded-2xl shadow-lg p-6">
            <Elements stripe={stripePromise} options={{ clientSecret }}>
              <CardSetupForm
                clientSecret={clientSecret}
                stripeAccountId={setupInfo.stripe_account_id}
                businessName={setupInfo.business_name}
                onSuccess={handleSuccess}
              />
            </Elements>
          </div>
        ) : (
          <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6 text-center">
            <AlertCircle size={32} className="text-amber-500 mx-auto mb-3" />
            <p className="text-amber-800 font-medium">Payment setup not available</p>
            <p className="text-amber-600 text-sm mt-1">
              Please contact {setupInfo.business_name} for alternative payment options.
            </p>
          </div>
        )}

        {/* Footer */}
        <div className="mt-8 text-center text-xs text-zinc-400">
          <p>© {new Date().getFullYear()} Powered by StreakFree</p>
        </div>
      </div>
    </div>
  );
};

export default PaymentSetupPage;
